import React from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Lock, X } from "lucide-react";
import JeaButton from "@/components/jea/JeaButton";

/**
 * PaywallModal — shown when a preview-mode user tries a gated action.
 * Consistent copy + CTA everywhere so we don't have this modal duplicated.
 */
const PaywallModal = ({ open, onClose, feature = "this feature", message = "" }) => {
  const nav = useNavigate();

  const resume = () => {
    nav("/onboarding");
    onClose?.();
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-[12px] z-[95]"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            className="fixed inset-0 z-[96] flex items-center justify-center px-4 py-6"
            data-testid="paywall-modal"
          >
            <div
              className="w-full max-w-[440px] bg-bg-secondary border border-border-default rounded-[16px] p-8 shadow-elevated"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-11 h-11 rounded-full bg-accent-pink/15 border border-accent-pink/40 flex items-center justify-center">
                  <Lock size={18} className="text-accent-pink" />
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="p-2 text-text-secondary hover:text-white"
                  data-testid="paywall-close"
                >
                  <X size={16} />
                </button>
              </div>
              <h2 className="text-white font-bold text-[18px]" data-testid="paywall-title">
                {message || `Complete onboarding to use ${feature}`}
              </h2>
              <p className="text-text-secondary text-[13px] mt-2 leading-6">
                You're in <strong className="text-white">preview mode</strong> — browsing is free, but
                creating debtors, running queries, and modifying billing require a finished setup.
                It takes about 2 minutes.
              </p>
              <div className="mt-6 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="h-11 px-5 rounded-[8px] border border-border-default text-text-secondary hover:text-white hover:bg-bg-tertiary text-[14px] font-semibold transition-colors"
                  data-testid="paywall-not-now"
                >
                  Not now
                </button>
                <JeaButton
                  onClick={resume}
                  className="gap-2"
                  data-testid="paywall-resume"
                >
                  <Sparkles size={14} /> Resume Onboarding
                </JeaButton>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default PaywallModal;
